import DeleteIcon from "@mui/icons-material/Delete";
import { CircularProgress, Modal, TextField, Tooltip } from "@mui/material";
import { useFormikContext } from "formik";
import React, { useState } from "react";

import CurrencyInput from "@/components/atoms/CurrencyInput";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import useGetProductById from "@/hooks/requests/product/use-get-product-by-id";
import { ICampaignItem } from "@/interfaces/campaign.interface";
import { CampaignFormPageMessages } from "@/messages/campaigns/campaign-form-page.messages";
import { CampaignType } from "@/types/campaign.types";

interface Props {
  item: ICampaignItem;
  index: number;
  messages: CampaignFormPageMessages;
}

const MAX_DESCRIPTION_LENGTH = 20;

export function ItemTableRow({ item, index, messages }: Props) {
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { values, setFieldValue } = useFormikContext<CampaignFormData>();
  const _id = item.productId || 0;
  const { data: product, isLoading: isLoadingProduct } = useGetProductById({ _id });
  const items = values?.items || [];

  const onRemove = (selectedItem: ICampaignItem) => {
    if (values.disabledField || values.isActiveStatus) return;
    if (selectedItem.id) {
      const removedItems = [...(values.removedItems || []), selectedItem.id];
      setFieldValue("removedItems", removedItems);

      const newItems = items.filter((item) => item.id !== selectedItem.id);
      setFieldValue("items", newItems);
    } else {
      const newItems = items.filter((item) => item.productCode !== selectedItem.productCode);
      setFieldValue("items", newItems);
    }
  };

  const updateQuantity = (productCode: string, newQuantity: number) => {
    const updatedItems = values.items.map((item) => {
      if (item.productCode === productCode) {
        return {
          ...item,
          minimumQuantity: newQuantity,
        };
      }
      return item;
    });

    setFieldValue("items", updatedItems);
  };

  const updatePrice = (productCode: string, promoPrice: number) => {
    const updatedItems = values.items.map((item) => {
      if (item.productCode === productCode) {
        return { ...item, promoPrice };
      }
      return item;
    });

    setFieldValue("items", updatedItems);
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseFloat(event.target.value);

    if (newQuantity <= 0 || isNaN(newQuantity)) {
      setQuantityError(messages.invalidPriceError);
    } else {
      setQuantityError(null);
      updateQuantity(item.productCode || "", newQuantity);
    }
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPrice = parseFloat(event.target.value);

    if (newPrice <= 0 || isNaN(newPrice)) {
      setPriceError(messages.invalidPriceError);
    } else {
      setPriceError(null);
      updatePrice(item.productCode || "", newPrice);
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <>
      <TableRow index={index}>
        <TableCell>
          <MDTypography variant="body2" color="text" fontWeight="regular">
            {item.productCode}
          </MDTypography>
        </TableCell>

        <TableCell>
          <MDTypography variant="body2" color="text" fontWeight="regular">
            {item.productName}
          </MDTypography>
        </TableCell>

        <TableCell>
          {isLoadingProduct ? (
            <CircularProgress />
          ) : (
            <>
              {product?.description && product.description.length > MAX_DESCRIPTION_LENGTH ? (
                <Tooltip title={messages.tooltipViewMore}>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="regular"
                    onClick={handleOpenModal}
                    style={{ cursor: "pointer" }}
                  >
                    {`${product.description.substring(0, MAX_DESCRIPTION_LENGTH)}...`}
                  </MDTypography>
                </Tooltip>
              ) : (
                <MDTypography variant="body2" color="text" fontWeight="regular">
                  {product?.description}
                </MDTypography>
              )}
            </>
          )}
        </TableCell>

        <TableCell>
          <TextField
            variant="standard"
            value={item.minimumQuantity}
            onChange={handleQuantityChange}
            type="number"
            error={!!quantityError}
            helperText={quantityError || ""}
            disabled={values.disabledField || values.isActiveStatus}
            slotProps={{
              htmlInput: { min: 1 },
            }}
          />
        </TableCell>

        <TableCell>
          <MDTypography variant="body2" color="text" fontWeight="regular">
            {isLoadingProduct ? (
              <CircularProgress size={20} />
            ) : (
              product?.skus?.reduce(
                (acc, sku) => acc + (Number(sku.stock) ?? 0),
                0
              ) ?? 0
            )}
          </MDTypography>
        </TableCell>

        <TableCell>
          <CurrencyInput
            label={messages.promotionalPriceLabel}
            value={item.promoPrice}
            fullWidth
            onChange={handlePriceChange}
            disabled={
              values.type !== CampaignType.PromotionalPrice ||
              values.disabledField ||
              values.isActiveStatus
            }
            InputLabelProps={{ shrink: true }}
            error={!!priceError}
            inputProps={{ min: 1 }}
            helperText={priceError || ""}
          />
        </TableCell>

        <TableCell onClick={() => onRemove(item)}>
          <MDTypography
            variant="body2"
            color="text"
            fontWeight="regular"
            style={{ cursor: "pointer" }}
          >
            <DeleteIcon fontSize="medium" />
          </MDTypography>
        </TableCell>
      </TableRow>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <MDTypography variant="body1" fontWeight={"bold"}>
            {messages.fullDescriptionLabel}
          </MDTypography>
          <MDTypography sx={{ mt: 2 }}>{product?.description}</MDTypography>
        </MDBox>
      </Modal>
    </>
  );
}
